import { useEffect, useState } from 'react';
import { buildStreamShipmentUrl } from '@/apis/homeAPI';
import { getActiveToken } from '@/common/helper/tokenStorage';
import { SSE_STALE_RECONNECT_SECONDS } from '../../HomeV2/map/mapboxMapHelpers';
import { getStreamBatchSize } from '../../Home/map/MapUtils';
import type { StreamShipment } from '../../Home/map/needsAttention';

/**
 * The location stream sends one full snapshot of the user's visible active
 * shipments and ends; the next snapshot is requested this long after.
 */
export const STREAM_REFRESH_MS = 90 * 1000;
const MAX_RETRY_MS = 30 * 1000;
/**
 * An open stream silent this long is treated as dead (a half-open connection
 * or a stuck backend sends neither `end` nor `error`), as on the flat map.
 */
export const STREAM_IDLE_MS = SSE_STALE_RECONNECT_SECONDS * 1000;
/**
 * Batches (20-100 shipments each) are applied together this often, as on
 * the flat map, so a snapshot re-clusters the globe a few times rather than
 * once per batch.
 */
export const STREAM_FLUSH_MS = 250;

/** Same rule as the dashboard map: numeric, non-zero coordinates. */
export const hasMapPosition = (s: StreamShipment): boolean =>
  typeof s?.latest_location?.latest_lat === 'number' &&
  typeof s?.latest_location?.latest_long === 'number' &&
  s.latest_location.latest_lat !== 0 &&
  s.latest_location.latest_long !== 0;

/** Batches update or add shipments; nothing is removed mid-snapshot. */
export const mergeBatch = (
  current: StreamShipment[],
  batch: StreamShipment[]
): StreamShipment[] => {
  const byId = new Map(current.map((s) => [String(s.shipment_id), s]));
  batch.forEach((s) => byId.set(String(s.shipment_id), s));
  return Array.from(byId.values());
};

/**
 * After a clean, complete snapshot, only the shipments in it stay: one that
 * left the active set (delivered, archived, moved team) drops off the TV.
 * Same array when nothing left, so nothing re-renders.
 */
export const pruneToSnapshot = (
  current: StreamShipment[],
  seenIds: Set<string>
): StreamShipment[] => {
  const kept = current.filter((s) => seenIds.has(String(s.shipment_id)));
  return kept.length === current.length ? current : kept;
};

/** The stream's team filter, as the dashboard builds it. */
export const teamQuery = (teamIds: string[]): string =>
  teamIds.length
    ? `teams=${teamIds.map((id) => encodeURIComponent(id)).join(',')}`
    : '';

export interface MonitorShipmentStream {
  shipments: StreamShipment[];
  /** in_transit_count of the latest valid batch; null until one arrives. */
  activeCount: number | null;
  /** Clean, complete snapshots received so far (the fleet has settled). */
  snapshots: number;
}

/**
 * The /monitor shipment feed: the same visibility-scoped stream as the
 * dashboard map, opened with the screen's team filter from the start (so no
 * company-wide snapshot is ever shown first). A malformed batch, a parse
 * error or a stream error makes that snapshot incomplete: it then neither
 * prunes, nor moves the live count.
 */
export const useMonitorShipmentStream = (
  token: string | null | undefined,
  teamIds: string[]
): MonitorShipmentStream => {
  const teamKey = teamIds.join(',');
  const [shipments, setShipments] = useState<StreamShipment[]>([]);
  const [activeCount, setActiveCount] = useState<number | null>(null);
  const [snapshots, setSnapshots] = useState(0);

  useEffect(() => {
    if (!token) return undefined;
    const query = teamQuery(teamKey ? teamKey.split(',') : []);
    let stopped = false;
    let source: EventSource | null = null;
    let timer: number | undefined;
    let idleTimer: number | undefined;
    let failures = 0;
    // Received but not yet applied: shipments by id, and the latest count.
    const pending = new Map<string, StreamShipment>();
    let pendingCount: number | null = null;
    let flushTimer: number | undefined;

    const flush = () => {
      window.clearTimeout(flushTimer);
      flushTimer = undefined;
      if (pendingCount !== null) {
        const count = pendingCount;
        pendingCount = null;
        setActiveCount(count);
      }
      if (pending.size) {
        const batch = Array.from(pending.values());
        pending.clear();
        setShipments((prev) => mergeBatch(prev, batch));
      }
    };
    const queue = (placed: StreamShipment[], count: number) => {
      placed.forEach((s) => pending.set(String(s.shipment_id), s));
      pendingCount = count;
      if (flushTimer === undefined)
        flushTimer = window.setTimeout(flush, STREAM_FLUSH_MS);
    };

    const schedule = (ms: number) => {
      window.clearTimeout(timer);
      if (!stopped) timer = window.setTimeout(connect, ms);
    };

    function connect() {
      if (stopped) return;
      const seenIds = new Set<string>();
      let complete = true;
      let failed = false;
      // The freshest token: the stored one is refreshed in the background.
      source = new EventSource(
        buildStreamShipmentUrl(
          getActiveToken() || (token as string),
          query,
          getStreamBatchSize()
        )
      );
      // Gives up on this snapshot (never pruned) and retries with backoff.
      const fail = () => {
        window.clearTimeout(idleTimer);
        // Positions already received are still good; only pruning is off.
        flush();
        complete = false;
        failed = true;
        source?.close();
        source = null;
        failures += 1;
        schedule(Math.min(MAX_RETRY_MS, 2000 * 2 ** failures));
      };
      const stillAlive = () => {
        window.clearTimeout(idleTimer);
        idleTimer = window.setTimeout(fail, STREAM_IDLE_MS);
      };
      stillAlive();
      source.onmessage = (event) => {
        try {
          const parsed = JSON.parse(event.data);
          const batch = parsed?.shipment_data;
          if (!Array.isArray(batch)) {
            complete = false;
            return;
          }
          // Only usable data counts as the stream being alive.
          stillAlive();
          failures = 0;
          batch.forEach((s: StreamShipment) =>
            seenIds.add(String(s?.shipment_id))
          );
          queue(batch.filter(hasMapPosition), parsed?.in_transit_count ?? 0);
        } catch {
          complete = false;
        }
      };
      source.addEventListener('end', () => {
        // The server sends `end` right after its own `event: error`: the
        // error's backoff retry stands, and the partial snapshot is ignored.
        if (failed) return;
        window.clearTimeout(idleTimer);
        source?.close();
        source = null;
        // Everything received is applied before the snapshot prunes.
        flush();
        if (complete) {
          setShipments((prev) => pruneToSnapshot(prev, seenIds));
          setSnapshots((count) => count + 1);
        }
        schedule(STREAM_REFRESH_MS);
      });
      // Network errors and the server's own `event: error` both land here.
      // Whatever arrives after an error, this snapshot never prunes.
      source.onerror = fail;
    }

    connect();
    return () => {
      stopped = true;
      window.clearTimeout(timer);
      window.clearTimeout(idleTimer);
      window.clearTimeout(flushTimer);
      source?.close();
    };
  }, [token, teamKey]);

  return { shipments, activeCount, snapshots };
};
